import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import './ShiftCommanderInterface.css';

import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useSocket } from '../contexts/SocketContext';
import { http } from '../lib/http';
import {
  Shift,
  User,
  BigBoardData,
  UnitType,
  Unit,
  UnitAssignment,
  LoggedInUser,
} from '../types';
import LoginScreen from './LoginScreen';

interface ShiftCommanderInterfaceProps {
  onClose: () => void;
}

const ShiftCommanderInterface: React.FC<ShiftCommanderInterfaceProps> = ({ onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [currentShift, setCurrentShift] = useState<Shift | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [bigBoardData, setBigBoardData] = useState<BigBoardData | null>(null);

  // Dialog states
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);

  const [shiftForm, setShiftForm] = useState({ id: '', name: '', startTime: '', endTime: '' });
  const [isEditingShift, setIsEditingShift] = useState(false);

  const [assignmentForm, setAssignmentForm] = useState({
    unitId: '',
    personnelId: '',
    role: '',
    freeformName: '',
    useFreeform: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const socket = useSocket();

  // ---------- Loaders (memoized to avoid stale closures) ----------
  const loadUsers = useCallback(async () => {
    const response = await http.get('/unit-assignments/users');
    if (response.data.success) setUsers(response.data.data);
  }, []);

  const loadShifts = useCallback(async () => {
    const response = await http.get('/unit-assignments/shifts');
    if (response.data.success) setShifts(response.data.data);
  }, []);

  const loadCurrentShift = useCallback(async () => {
    const response = await http.get('/unit-assignments/shifts/current');
    if (response.data.success) setCurrentShift(response.data.data);
  }, []);

  const loadUnits = useCallback(async () => {
    const response = await http.get('/units');
    if (response.data.success) setUnits(response.data.data);
  }, []);

  const loadUnitTypes = useCallback(async () => {
    const response = await http.get('/unit-assignments/unit-types');
    if (response.data.success) setUnitTypes(response.data.data);
  }, []);

  const loadBigBoardData = useCallback(async () => {
    try {
      const response = await http.get('/unit-assignments/big-board');
      if (response.data.success) {
        // Ensure the data has the expected structure
        const data = response.data.data;
        if (data && typeof data === 'object') {
          setBigBoardData({
            shift: data.shift || null,
            assignments: data.assignments || {},
            crossStaffing: data.crossStaffing || {},
            loggedInUsers: Array.isArray(data.loggedInUsers) ? data.loggedInUsers : [],
            claimedUnits: data.claimedUnits || {},
            units: Array.isArray(data.units) ? data.units : []
          });
        } else {
          console.error('Invalid big board data structure:', data);
          setBigBoardData(null);
        }
      } else {
        console.error('Failed to load big board data:', response.data.error);
        setBigBoardData(null);
      }
    } catch (error) {
      console.error('Error loading big board data:', error);
      setBigBoardData(null);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadUsers(),
        loadShifts(),
        loadCurrentShift(),
        loadUnits(),
        loadUnitTypes(),
        loadBigBoardData(),
      ]);
      setError('');
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [loadUsers, loadShifts, loadCurrentShift, loadUnits, loadUnitTypes, loadBigBoardData]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadData();
    }
  }, [isAuthenticated, user, loadData]);

  // ---------- Socket live updates (stable handlers) ----------
  useEffect(() => {
    if (!socket) return;

    const handleShiftUpdate = () => {
      loadCurrentShift();
      loadShifts();
      loadBigBoardData();
    };
    const handleAssignmentUpdate = () => {
      loadBigBoardData();
    };
    const handleConnect = () => {
      // join broadcast room when connected & authed
      if (isAuthenticated) socket.emit('join-dispatch');
    };

    socket.on('connect', handleConnect);
    socket.on('shift-updated', handleShiftUpdate);
    socket.on('shift-activated', handleShiftUpdate);
    socket.on('shift-deleted', handleShiftUpdate);
    socket.on('shift-created', handleShiftUpdate);
    socket.on('unit-assignment-updated', handleAssignmentUpdate);
    socket.on('unit-assignment-removed', handleAssignmentUpdate);

    if (socket.connected && isAuthenticated) {
      socket.emit('join-dispatch');
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('shift-updated', handleShiftUpdate);
      socket.off('shift-activated', handleShiftUpdate);
      socket.off('shift-deleted', handleShiftUpdate);
      socket.off('shift-created', handleShiftUpdate);
      socket.off('unit-assignment-updated', handleAssignmentUpdate);
      socket.off('unit-assignment-removed', handleAssignmentUpdate);
    };
  }, [socket, isAuthenticated, loadCurrentShift, loadShifts, loadBigBoardData]);

  // ---------- Actions ----------
  const handleCreateShift = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!shiftForm.name || !shiftForm.startTime || !shiftForm.endTime) {
        setError('All fields are required');
        return;
      }

      const payload = {
          name: shiftForm.name,
          startTime: new Date(shiftForm.startTime).toISOString(),
          endTime: new Date(shiftForm.endTime).toISOString(),
      };
        
      if (isEditingShift && shiftForm.id) {
        const response = await http.put(`/unit-assignments/shifts/${shiftForm.id}`, payload);
        if (response.data.success) {
          await loadShifts();
          await loadCurrentShift();
          setShiftDialogOpen(false);
          setShiftForm({ id: '', name: '', startTime: '', endTime: '' });
          setIsEditingShift(false);
          setError('');
        }
      } else {
        const response = await http.post('/unit-assignments/shifts', payload);
        if (response.data.success) {
          await loadShifts();
          await loadCurrentShift();
          setShiftDialogOpen(false);
          setShiftForm({ id: '', name: '', startTime: '', endTime: '' });
          setError('');
        }
      }
    } catch (error: any) {
      const errorMsg = typeof error?.response?.data?.error === 'string'
        ? error.response.data.error
        : `Failed to ${isEditingShift ? 'update' : 'create'} shift`;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [shiftForm, isEditingShift, loadShifts, loadCurrentShift]);

  const handleEditShift = useCallback((shift: Shift) => {
    const startDate = new Date(shift.startTime);
    const endDate = new Date(shift.endTime);
    const localStart = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    const localEnd = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setShiftForm({ id: shift.id, name: shift.name, startTime: localStart, endTime: localEnd });
    setIsEditingShift(true);
    setShiftDialogOpen(true);
  }, []);

  const handleDeleteShift = useCallback(async (shiftId: string) => {
    if (!window.confirm('Delete this shift? This will also remove all assignments.')) return;
    try {
      setLoading(true);
      const response = await http.delete(`/unit-assignments/shifts/${shiftId}`);
      if (response.data.success) {
        await loadShifts();
        await loadCurrentShift();
      }
    } catch (error: any) {
      const errorMsg = typeof error?.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to delete shift';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [loadShifts, loadCurrentShift]);

  const handleToggleShiftActive = useCallback(async (shiftId: string, isActive: boolean) => {
    try {
      setLoading(true);
      const response = await http.patch(`/unit-assignments/shifts/${shiftId}/activate`, { isActive });
      if (response.data.success) {
        await loadShifts();
        await loadCurrentShift();
      }
    } catch (error: any) {
      const errorMsg = typeof error?.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to update shift status';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [loadShifts, loadCurrentShift]);

  const handleCreateAssignment = useCallback(async () => {
    if (!bigBoardData?.shift) return;
    try {
      setLoading(true);
      
      // If using freeform, don't send userId (no auto-claiming)
      const requestData: any = {
        unitId: assignmentForm.unitId,
        role: assignmentForm.role,
      };
      
      if (assignmentForm.useFreeform) {
        requestData.freeformName = assignmentForm.freeformName;
      } else {
        requestData.userId = assignmentForm.personnelId;
      }
      
      const response = await http.post(`/unit-assignments/shifts/${bigBoardData.shift.id}/assignments`, requestData);
      if (response.data.success) {
        await loadBigBoardData();
        setAssignmentDialogOpen(false);
        setAssignmentForm({ unitId: '', personnelId: '', role: '', freeformName: '', useFreeform: false });
      }
    } catch (error: any) {
      const errorMsg = typeof error?.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to create assignment';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [assignmentForm, bigBoardData?.shift, loadBigBoardData]);

  const handleRemoveAssignment = useCallback(async (assignmentId: string) => {
    if (!bigBoardData?.shift) return;
    try {
      setLoading(true);
      await http.delete(`/unit-assignments/shifts/${bigBoardData.shift.id}/assignments/${assignmentId}`);
      await loadBigBoardData();
    } catch (error: any) {
      const errorMsg = typeof error?.response?.data?.error === 'string'
        ? error.response.data.error
        : 'Failed to remove assignment';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [bigBoardData?.shift, loadBigBoardData]);

  // ---------- Helpers ----------
  const getUnitTypeForUnit = useCallback((unitId: string) => {
    const unit = units.find(u => u.id === unitId);
    return unitTypes.find(ut => ut.id === unit?.type);
  }, [units, unitTypes]);

  const getAvailableRolesForUnit = useCallback((unitId: string) => {
    const unitType = getUnitTypeForUnit(unitId);
    if (!unitType) return [];
    return [...unitType.requiredRoles, ...unitType.optionalRoles];
  }, [getUnitTypeForUnit]);

  const getAvailableUsersForRole = useCallback((_role: string) => users, [users]);

  // Safer access if API returns either map or flat list
  const getAssignmentsForUnit = useCallback((unitId: string): UnitAssignment[] => {
    const a = (bigBoardData?.assignments as any);
    if (!a) return [];
    if (Array.isArray(a)) return a.filter(x => x.unitId === unitId);
    return Array.isArray(a[unitId]) ? a[unitId] : [];
  }, [bigBoardData?.assignments]);

  const getCrossStaffingForUnit = useCallback((unitId: string) => {
    const cs = (bigBoardData?.crossStaffing as any);
    if (!cs) return [];
    if (Array.isArray(cs)) return cs.filter((x: any) => x.unitId === unitId);
    return Array.isArray(cs[unitId]) ? cs[unitId] : [];
  }, [bigBoardData?.crossStaffing]);

  // Helper function to get users without assignments
  const getUsersWithoutAssignments = useCallback(() => {
    if (!bigBoardData?.loggedInUsers || !Array.isArray(bigBoardData.loggedInUsers)) return [];
    
    return bigBoardData.loggedInUsers.filter(user => {
      // Safety check for user object
      if (!user || typeof user !== 'object') return false;
      
      // Check if user has any assignments for current shift
      if (bigBoardData.shift) {
        const hasAssignment = Object.values(bigBoardData.assignments || {}).some(assignments => 
          Array.isArray(assignments) && assignments.some(assignment => assignment?.userId === user.id)
        );
        return !hasAssignment;
      }
      // If no active shift, show all logged in users without claimed units
      return !user.claimedUnit;
    });
  }, [bigBoardData]);

  // Helper function to get claimed units with their users
  const getClaimedUnitsData = useCallback(() => {
    if (!bigBoardData?.claimedUnits || typeof bigBoardData.claimedUnits !== 'object') return [];
    
    const claimedUnitsList: any[] = [];
    Object.entries(bigBoardData.claimedUnits).forEach(([unitId, users]) => {
      if (Array.isArray(users) && users.length > 0) {
        const unit = units.find(u => u.id === unitId);
        if (unit) {
          claimedUnitsList.push({
            ...unit,
            claimedBy: users,
            claims: users
          });
        }
      }
    });
    
    return claimedUnitsList;
  }, [bigBoardData?.claimedUnits, units]);

  // ---------- Renderers ----------
  const renderPersonnelManagement = useCallback(() => (
    <div>
      <div className="section-header">
        <div>
          <div className="section-title">CAD Users — Available Personnel</div>
        </div>
      </div>

      {users.length === 0 && <div className="alert warning">No users found in the system.</div>}

      <div className="card-grid">
        {users.map((u) => (
          <div key={u.id} className="user-card">
            <div className="card-title">{u.username}</div>
            <div className="card-subtitle">{u.robloxName || 'No Roblox Name'}</div>
            <div className="chip primary" style={{ marginBottom: '12px' }}>{u.role}</div>
            {u.email && (
              <div className="card-meta" style={{ marginBottom: '8px' }}>
                {u.email}
              </div>
            )}
            <div className="card-meta">
              Status: {u.isActive ? 'Active' : 'Inactive'}
            </div>
          </div>
        ))}
      </div>
    </div>
  ), [users]);

  const renderShiftManagement = useCallback(() => (
    <div>
      <div className="section-header">
        <div>
          <div className="section-title">Shift Management</div>
          <div className="section-subtitle">
            Times displayed in {Intl.DateTimeFormat().resolvedOptions().timeZone}
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            const now = new Date();
            const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
            setShiftForm((s) => ({ ...s, startTime: local }));
            setShiftDialogOpen(true);
          }}
        >
          <Icon icon="material-symbols:add" width="18" height="18" />
          Create Shift
        </button>
      </div>

      {currentShift && (
        <div className="alert info">
          Current Shift: {currentShift.name} ({new Date(currentShift.startTime).toLocaleDateString()} - {new Date(currentShift.endTime).toLocaleDateString()})
        </div>
      )}

      <div className="card-grid">
        {shifts.map((shift) => (
          <div key={shift.id} className="shift-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div className="card-title">{shift.name}</div>
              <div className={shift.isActive ? 'chip success' : 'chip outlined'}>
                {shift.isActive ? 'Active' : 'Inactive'}
              </div>
            </div>
            <div className="card-subtitle" style={{ marginBottom: '16px' }}>
              {new Date(shift.startTime).toLocaleString()} — {new Date(shift.endTime).toLocaleString()}
            </div>
            <div className="btn-group">
              <button className="btn btn-outlined btn-small" onClick={() => handleEditShift(shift)}>
                <Icon icon="material-symbols:edit" width="16" height="16" />
                Edit
              </button>
              <button
                className={`btn btn-small ${shift.isActive ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => handleToggleShiftActive(shift.id, !shift.isActive)}
              >
                {shift.isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button className="btn btn-danger btn-small" onClick={() => handleDeleteShift(shift.id)}>
                <Icon icon="material-symbols:delete" width="16" height="16" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  ), [currentShift, shifts, handleEditShift, handleToggleShiftActive, handleDeleteShift]);

  const renderBigBoard = useCallback(() => {
    // Safety check for bigBoardData
    if (!bigBoardData) {
      return (
        <div>
          <div className="section-header">
            <div className="section-title">Big Board View</div>
          </div>
          <div className="alert warning">Loading big board data...</div>
        </div>
      );
    }

    const isShiftActive = bigBoardData?.shift?.isActive;
    const unassignedUsers = getUsersWithoutAssignments();
    const claimedUnits = getClaimedUnitsData();

    return (
      <div>
        <div className="section-header">
          <div className="section-title">Big Board View</div>
          {bigBoardData?.shift?.isActive && (
            <button className="btn btn-primary" onClick={() => setAssignmentDialogOpen(true)}>
              <Icon icon="material-symbols:add" width="18" height="18" />
              Add Assignment
            </button>
          )}
        </div>

        {bigBoardData?.shift ? (
          <div className="alert info">
            Current Shift: {bigBoardData.shift.name} 
            {isShiftActive ? ' (Active)' : ' (Inactive)'}
          </div>
        ) : (
          <div className="alert warning">No active shift. Create a shift to see unit assignments.</div>
        )}

        {/* Available Personnel Section */}
        {unassignedUsers.length > 0 && (
          <div>
            <div className="section-header">
              <div className="section-title">Available Personnel</div>
            </div>
            <div className="big-board-grid">
              {unassignedUsers.map((user) => (
                <div key={`user-${user.id}`} className="unit-card available">
                  <div className="unit-id badge available">
                    {user.robloxName || user.username}
                  </div>
                  <div className="unit-meta">Available for Assignment</div>
                  <div className="divider"></div>
                  <div className="role-section">
                    <div className="role-item">
                      <div className="role-label">Username:</div>
                      <div className="role-assignment">
                        <div className="chip primary">{user.username}</div>
                      </div>
                    </div>
                    <div className="role-item">
                      <div className="role-label">Role:</div>
                      <div className="role-assignment">
                        <div className="chip outlined">{user.role}</div>
                      </div>
                    </div>
                    {user.claimedUnit && (
                      <div className="role-item">
                        <div className="role-label">Claimed Unit:</div>
                        <div className="role-assignment">
                          <div className="chip warning">{user.claimedUnit}</div>
                        </div>
                      </div>
                    )}
                    {bigBoardData?.shift?.isActive && (
                      <div className="role-item">
                        <div className="role-label">Status:</div>
                        <div className="role-assignment">
                          <div className="chip success">Ready for Assignment</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Claimed Units Section (when shift is not active) */}
        {!isShiftActive && claimedUnits.length > 0 && (
          <div>
            <div className="section-header">
              <div className="section-title">Claimed Units</div>
            </div>
            <div className="big-board-grid">
              {claimedUnits.map((unit) => (
                <div key={unit.id} className="unit-card claimed">
                  <div className="unit-id badge warning">
                    {unit.id}
                  </div>
                  <div className="unit-meta">{unit.type} — {unit.status}</div>
                  <div className="divider"></div>
                  <div className="role-section">
                    <div className="role-title">Claimed By:</div>
                    {unit.claimedBy.map((user: LoggedInUser, idx: number) => (
                      <div key={idx} className="role-item">
                        <div className="role-label">User:</div>
                        <div className="role-assignment">
                          <div className="chip success">
                            {user.robloxName || user.username} ({user.role})
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Regular Unit Assignments */}
        <div>
          <div className="section-header">
            <div className="section-title">
              Unit Assignments { !isShiftActive && <span className="read-only-chip" style={{ marginLeft: 8 }}>READ ONLY</span> }
            </div>
          </div>
          <div className="big-board-grid">
            {units
              .filter((unit) => {
                const unitAssignments = getAssignmentsForUnit(unit.id);
                const unitCrossStaffing = getCrossStaffingForUnit(unit.id);
                // Show unit if it has any assignments OR cross-staffing
                return unitAssignments.length > 0 || unitCrossStaffing.length > 0;
              })
              .map((unit) => {
                const unitAssignments = getAssignmentsForUnit(unit.id);
                const unitCrossStaffing = getCrossStaffingForUnit(unit.id);
                const unitType = getUnitTypeForUnit(unit.id);
                
                return (
                  <div 
                    key={unit.id} 
                    className={`unit-card ${!isShiftActive ? 'read-only' : ''}`}
                  >
                    <div className="unit-id">{unit.id}</div>
                    <div className="unit-meta">{unit.type} — {unit.status}</div>

                    <div className="divider"></div>
                        
                    {unitType && (
                      <div className="role-section">
                        <div className="role-title">Required Roles:</div>
                        {unitType.requiredRoles.map((role) => {
                          const assignment = unitAssignments.find(a => a.role === role);
                          return (
                            <div key={role} className="role-item">
                              <div className="role-label">{role}:</div>
                              <div className="role-assignment">
                                {assignment ? (
                                  <>
                                    <div className="chip success">
                                      {assignment.callsign.startsWith('FREE_') ? assignment.personnelName : `${assignment.callsign} (${assignment.personnelName})`}
                                    </div>
                                    {isShiftActive && (
                                      <button className="remove-btn" onClick={() => handleRemoveAssignment(assignment.id)}>
                                        <Icon icon="material-symbols:delete" width="16" height="16" />
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  <div className="chip warning">Unassigned</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        <div className="role-title" style={{ marginTop: '16px' }}>Optional Roles:</div>
                        {unitType.optionalRoles.map((role) => {
                          const assignment = unitAssignments.find(a => a.role === role);
                          return (
                            <div key={role} className="role-item">
                              <div className="role-label">{role}:</div>
                              <div className="role-assignment">
                                {assignment ? (
                                  <>
                                    <div className="chip primary">
                                      {assignment.callsign.startsWith('FREE_') ? assignment.personnelName : `${assignment.callsign} (${assignment.personnelName})`}
                                    </div>
                                    {isShiftActive && (
                                      <button className="remove-btn" onClick={() => handleRemoveAssignment(assignment.id)}>
                                        <Icon icon="material-symbols:delete" width="16" height="16" />
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  <div className="chip outlined">Unassigned</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {unitCrossStaffing.length > 0 && (
                      <div className="cross-staffing">
                        <div className="divider"></div>
                        <div className="cross-staffing-title">Cross-Staffing:</div>
                        {unitCrossStaffing.map((cs: any, idx: number) => (
                          <div key={cs.id ?? `${cs.crossUnitId}-${idx}`} className="cross-staffing-item">
                            {cs.crossUnitId}: {cs.callsign}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    );
  }, [bigBoardData?.shift, units, getAssignmentsForUnit, getCrossStaffingForUnit, getUnitTypeForUnit, handleRemoveAssignment, getUsersWithoutAssignments, getClaimedUnitsData]);

  // ---------- Auth gates ----------
  if (authLoading) {
    return (
      <div className="shift-commander-overlay">
        <div className="shift-commander-container">
          <div className="loading">
            <div className="loading-spinner"></div>
            Verifying authentication...
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) return <LoginScreen />;

  // Check loading states first
  if (authLoading || permissionsLoading) {
    return (
      <div className="shift-commander-overlay">
        <div className="shift-commander-container">
          <div className="loading">Loading permissions...</div>
        </div>
      </div>
    );
  }

  // Use secure server-side permission check instead of client-side role check
  if (!hasPermission('shift-commander')) {
    return (
      <div className="shift-commander-overlay">
        <div className="shift-commander-container">
          <div className="error-alert">
            Access Denied: You need admin, dispatcher, or command privileges to access the Shift Commander interface.
          </div>
        </div>
      </div>
    );
  }

  // ---------- Layout: modern popup style ----------
  return (
    <div className="shift-commander-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="shift-commander-container">
        {/* Header */}
        <div className="shift-commander-header">
          <h2>
            <Icon icon="material-symbols:people" width="24" height="24" style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Shift Commander Interface
          </h2>
          <button className="close-button" onClick={onClose}>
            <Icon icon="material-symbols:close" width="20" height="20" />
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="error-alert" style={{ margin: '0 28px', marginTop: '20px', marginBottom: '20px' }}>
            {error}
            <button 
              onClick={() => setError('')} 
              style={{ 
                float: 'right', 
                background: 'none', 
                border: 'none', 
                color: 'inherit', 
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="tabs-container">
          <ul className="tabs-list">
            <li className="tab-item">
              <button
                className={`tab-button ${tabValue === 0 ? 'active' : ''}`}
                onClick={() => setTabValue(0)}
              >
                <Icon icon="material-symbols:people" width="18" height="18" />
                CAD Users
              </button>
            </li>
            <li className="tab-item">
              <button
                className={`tab-button ${tabValue === 1 ? 'active' : ''}`}
                onClick={() => setTabValue(1)}
              >
                <Icon icon="material-symbols:schedule" width="18" height="18" />
                Shifts
              </button>
            </li>
            <li className="tab-item">
              <button
                className={`tab-button ${tabValue === 2 ? 'active' : ''}`}
                onClick={() => setTabValue(2)}
              >
                Big Board
              </button>
            </li>
          </ul>
        </div>

        {/* Content Area */}
        <div className="shift-commander-content">
          <div className={`tab-panel ${tabValue === 0 ? 'active' : ''}`}>
            {renderPersonnelManagement()}
          </div>
          <div className={`tab-panel ${tabValue === 1 ? 'active' : ''}`}>
            {renderShiftManagement()}
          </div>
          <div className={`tab-panel ${tabValue === 2 ? 'active' : ''}`}>
            {renderBigBoard()}
          </div>
        </div>

        {/* Shift Dialog */}
        {shiftDialogOpen && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShiftDialogOpen(false);
              setIsEditingShift(false);
              setShiftForm({ id: '', name: '', startTime: '', endTime: '' });
            }
          }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{isEditingShift ? 'Edit Shift' : 'Create Shift'}</h3>
                <button onClick={() => {
                  setShiftDialogOpen(false);
                  setIsEditingShift(false);
                  setShiftForm({ id: '', name: '', startTime: '', endTime: '' });
                }}>
                  <Icon icon="material-symbols:close" width="20" height="20" />
                </button>
              </div>
              <div className="modal-body">
                <div style={{ marginBottom: '16px', fontSize: '12px', color: '#aaa' }}>
                  Times in {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </div>
                
                <div className="form-group">
                  <label>Shift Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={shiftForm.name}
                    onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })}
                    placeholder="e.g., Morning Shift, Night Shift, Day 1"
                  />
                </div>
                
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={shiftForm.startTime}
                    onChange={(e) => setShiftForm({ ...shiftForm, startTime: e.target.value })}
                    step="300"
                  />
                </div>
                
                <div className="form-group">
                  <label>End Time</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={shiftForm.endTime}
                    onChange={(e) => setShiftForm({ ...shiftForm, endTime: e.target.value })}
                    step="300"
                  />
                </div>
                
                <div className="form-group">
                  <button
                    className="btn btn-outlined"
                    onClick={() => {
                      if (shiftForm.startTime) {
                        const baseTime = shiftForm.endTime ? new Date(shiftForm.endTime) : new Date(shiftForm.startTime);
                        const newEndTime = new Date(baseTime.getTime() + 60 * 60 * 1000);
                        const local = new Date(newEndTime.getTime() - newEndTime.getTimezoneOffset() * 60000)
                          .toISOString()
                          .slice(0, 16);
                        setShiftForm({ ...shiftForm, endTime: local });
                      }
                    }}
                    disabled={!shiftForm.startTime}
                    style={{ width: '100%' }}
                  >
                    Set End Time (+1 Hour)
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outlined"
                  onClick={() => {
                    setShiftDialogOpen(false);
                    setIsEditingShift(false);
                    setShiftForm({ id: '', name: '', startTime: '', endTime: '' });
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleCreateShift} 
                  disabled={loading || !shiftForm.name || !shiftForm.startTime || !shiftForm.endTime}
                >
                  {loading ? (isEditingShift ? 'Updating...' : 'Creating...') : (isEditingShift ? 'Update' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assignment Dialog */}
        {assignmentDialogOpen && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
              setAssignmentDialogOpen(false);
              setAssignmentForm({ unitId: '', personnelId: '', role: '', freeformName: '', useFreeform: false });
            }
          }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Add Unit Assignment</h3>
                <button onClick={() => {
                  setAssignmentDialogOpen(false);
                  setAssignmentForm({ unitId: '', personnelId: '', role: '', freeformName: '', useFreeform: false });
                }}>
                  <Icon icon="material-symbols:close" width="20" height="20" />
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Unit</label>
                  <select
                    className="form-select"
                    value={assignmentForm.unitId}
                    onChange={(e) => {
                      setAssignmentForm({ ...assignmentForm, unitId: e.target.value, role: '' });
                    }}
                  >
                    <option value="">Select a unit</option>
                    {units.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.id} ({unit.type})
                      </option>
                    ))}
                  </select>
                </div>

                {assignmentForm.unitId && (
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      className="form-select"
                      value={assignmentForm.role}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, role: e.target.value, personnelId: '' })}
                    >
                      <option value="">Select a role</option>
                      {getAvailableRolesForUnit(assignmentForm.unitId).map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {assignmentForm.role && (
                  <div className="form-checkbox">
                    <input
                      type="checkbox"
                      id="useFreeform"
                      checked={assignmentForm.useFreeform}
                      onChange={(e) => setAssignmentForm({ 
                        ...assignmentForm, 
                        useFreeform: e.target.checked,
                        personnelId: e.target.checked ? '' : assignmentForm.personnelId,
                        freeformName: e.target.checked ? assignmentForm.freeformName : ''
                      })}
                    />
                    <label htmlFor="useFreeform">Use freeform name (not in CAD system)</label>
                  </div>
                )}

                {assignmentForm.role && !assignmentForm.useFreeform && (
                  <div className="form-group">
                    <label>User</label>
                    <select
                      className="form-select"
                      value={assignmentForm.personnelId}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, personnelId: e.target.value })}
                    >
                      <option value="">Select a user</option>
                      {getAvailableUsersForRole(assignmentForm.role).map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.robloxName || user.username} (@{user.username})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {assignmentForm.role && assignmentForm.useFreeform && (
                  <div className="form-group">
                    <label>Personnel Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={assignmentForm.freeformName}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, freeformName: e.target.value })}
                      placeholder="Enter name of person not in CAD system"
                    />
                    <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                      This will not auto-claim the unit for anyone
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-outlined"
                  onClick={() => {
                    setAssignmentDialogOpen(false);
                    setAssignmentForm({ unitId: '', personnelId: '', role: '', freeformName: '', useFreeform: false });
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateAssignment}
                  disabled={
                    loading || 
                    !assignmentForm.unitId || 
                    !assignmentForm.role || 
                    (!assignmentForm.useFreeform && !assignmentForm.personnelId) ||
                    (assignmentForm.useFreeform && !assignmentForm.freeformName.trim())
                  }
                >
                  {loading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftCommanderInterface;
